<script>
import APIMemberships from '@school/services/API/Memberships';
import utilsMixin from '@shared/mixins/utils';
import MembershipSections from '@school/components/MembershipSections.vue';
import ConfirmationPrompt from '@school/components/ConfirmationPrompt.vue';
import MembershipCancelPreviewModal from '@school/components/MembershipCancelPreviewModal.vue';
import RegisterForm from '@school/components/RegisterForm.vue';
import buildMembershipPriceBuyable from '@school/utils/membership';

export default {
  mixins: [utilsMixin],
  components: {
    MembershipSections,
  },
  head() {
    return {
      title: this.$t('account_memberships.page_title'),
    };
  },
  data() {
    return {
      isLoading: true,
      memberships: [],
    };
  },
  async created() {
    try {
      this.$root.$on('customer-memberships:refresh', this.loadMemberships);
      await this.loadMemberships();
    } catch (error) {
      console.error('Error in created hook:', error);
    } finally {
      this.isLoading = false;
    }
  },
  beforeDestroy() {
    this.$root.$off('customer-memberships:refresh', this.loadMemberships);
  },
  computed: {
    confirmationText() {
      return this.$t('account_memberships.cancel.confirmation_text');
    },
  },
  methods: {
    async loadMemberships() {
      try {
        if (!this.isLoading) {
          this.isLoading = true;
        }
        const response = await APIMemberships.getMyMemberships();
        // Handle paginated response - data is in response.data.data
        const membershipsData = response.data?.data || [];
        this.memberships = Array.isArray(membershipsData) ? membershipsData : [];
      } catch (error) {
        this.$showMessage.error(this.$t('account_memberships.errors.load_memberships'));
        this.memberships = []; // Ensure array on error
        console.error('Error loading memberships:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async cancelMembership(membership) {
      const openConfirmationModal = () => {
        const title = this.$t('account_memberships.cancel.title');
        const message = this.$t('account_memberships.cancel.message', {
          name: membership.membership.name,
          confirmationText: this.confirmationText,
        });
        const confirmText = this.$t('account_memberships.cancel.confirm_button');
        const successMessage = this.$t('account_memberships.cancel.success');
        const cancelErrorMessage = this.$t('account_memberships.cancel.error');
        const cancelText = this.$t('account_memberships.cancel.cancel_button');

        let modalInstance;

        const closeModal = () => {
          if (modalInstance) {
            modalInstance.close();
          }
        };

        const props = {
          title,
          message,
          confirmationText: this.confirmationText,
          confirmText,
          cancelText,
          type: 'is-warning',
        };

        const events = {
          cancel: closeModal,
          close: closeModal,
          confirm: async({ setProcessing }) => {
            try {
              setProcessing(true);
              await APIMemberships.cancelMembership(membership.uuid);
              await this.loadMemberships();
              this.$showMessage.success(successMessage);
            } catch (error) {
              this.$showMessage.error(cancelErrorMessage);
            } finally {
              setProcessing(false);
              closeModal();
            }
          },
        };

        modalInstance = this.$buefy.modal.open({
          parent: this,
          component: ConfirmationPrompt,
          hasModalCard: true,
          trapFocus: true,
          props,
          events,
        });
      };

      let previewInstance;

      const closePreview = () => {
        if (previewInstance) {
          previewInstance.close();
        }
      };

      previewInstance = this.$buefy.modal.open({
        parent: this,
        component: MembershipCancelPreviewModal,
        hasModalCard: true,
        trapFocus: true,
        width: '560px',
        props: {
          membership,
        },
        events: {
          confirm: () => {
            closePreview();
            openConfirmationModal();
          },
          cancel: closePreview,
          close: closePreview,
        },
      });
    },
    async reactivateMembership(membership, controls) {
      try {
        controls?.start?.();
        await APIMemberships.resumeMembership(membership.uuid);
        await this.loadMemberships();
        this.$showMessage.success(this.$t('account_memberships.reactivate.success'));
      } catch (error) {
        this.$showMessage.error(this.$t('account_memberships.reactivate.error'));
      } finally {
        controls?.stop?.();
      }
    },
    async rejoindMembership(membership, controls) {
      controls?.stop?.();

      const membershipData = membership.membership;
      const priceUuid = membership.price?.uuid;
      const buyable = buildMembershipPriceBuyable(membershipData, priceUuid);

      if (!buyable) {
        this.$buefy.toast.open({
          message: this.$t('account_memberships.no_active_prices'),
          type: 'is-danger',
        });
        return;
      }

      const successMessage = this.$t('account_memberships.resume.success');

      const modalInstance = this.$buefy.modal.open({
        parent: this,
        component: RegisterForm,
        props: { buyable },
        hasModalCard: false,
        canCancel: ['escape', 'outside'],
        width: '520px',
        events: {
          enroll: () => {
            modalInstance.close();
            this.loadMemberships();
            this.$showMessage.success(successMessage);
          },
          complete: () => {
            modalInstance.close();
          },
          close: () => {
            modalInstance.close();
          },
        },
      });
    },
  },
};
</script>

<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoading" class="has-text-centered py-6">
      <b-loading :active="isLoading" />
    </div>

    <!-- Content when not loading -->
    <div v-else>
      <MembershipSections
        :memberships="memberships"
        @cancel="cancelMembership"
        @reactivate="reactivateMembership"
        @rejoin="rejoindMembership"
      />
    </div>
  </div>
</template>
